/**
 * Outreach vocabulary and rules: the cold-email queue to YC founders, sent
 * from justin@snowballrank.com. The shapes mirror the API
 * (Gluska-Forge-API, OutreachPresenter); the helpers are plain functions with
 * no React so tests/outreach.test.mjs can pin them.
 *
 * A prospect moves candidate → approved → drafted → scheduled → sent →
 * linkedin_invited → linkedin_accepted → replied, or is skipped before a
 * reply. An agent queues candidates each weekday morning, someone with access
 * approves or skips them, a research agent drafts the approved ones, and a
 * person approves each send: Forge sends it from Gmail at its slot (at least an
 * hour later, inside the sending window) and brings replies back into the
 * thread. Agents never send. Copy-and-mark-sent still works for a send by hand.
 */
import type { StatusTone } from "@/lib/studio"

export type OutreachStage =
  | "candidate"
  | "approved"
  | "drafted"
  | "scheduled"
  | "sent"
  | "linkedin_invited"
  | "linkedin_accepted"
  | "replied"
  | "skipped"
  | "backlog"
export type OutreachList = "just_raised" | "new_yc"
export type RaiseWindow = "too_early" | "good" | "late" | "unknown"
export type SeoStrength = "weak" | "some" | "strong"
export type ReplyChannel = "email" | "linkedin"
export type Handoff = "waiting" | "with_justin" | "converted"
export type OutreachAction =
  | "approve"
  | "drafted"
  | "schedule_send"
  | "cancel_send"
  | "mark_sent"
  | "linkedin_invited"
  | "linkedin_accepted"
  | "followup_sent"
  | "replied"
  | "skip"
  | "hand_to_justin"
  | "convert_to_prospect"
  | "reopen"
  | "hold_research"
  | "resume_research"
  | "clear_inbox"
  | "pull"
  | "back_to_backlog"

/** The tab strip: Today plus one tab per stage, the two LinkedIn stages as one. */
export type OutreachTab = "today" | "inbox" | "candidate" | "approved" | "drafted" | "scheduled" | "sent" | "linkedin" | "replied" | "skipped" | "backlog"
export type TodayGroupKey = "inbox" | "review" | "researching" | "send" | "scheduled" | "invite" | "followup"

export interface OutreachUserRef {
  id: number
  name: string
}

export interface OutreachEvent {
  id: number
  type: string
  user: OutreachUserRef | null
  /** token: an agent (or anyone) calling the API with a token; session: a person in the browser. */
  via: "token" | "session"
  meta: Record<string, unknown>
  created_at: string
}

export interface OutreachProspect {
  id: number
  company: string
  domain: string | null
  website: string | null
  one_liner: string | null
  list_type: OutreachList
  yc_batch: string | null
  founded_year: number | null
  company_age_years: number | null
  team_size: number | null
  raise_amount: string | null
  raise_round: string | null
  raised_on: string | null
  raise_source: string | null
  raise_verified: boolean
  total_raised: string | null
  days_since_raise: number | null
  raise_window: RaiseWindow
  content_note: string | null
  organic_keywords: number | null
  organic_etv: number | null
  organic_top10: number | null
  blog_posts: number | null
  last_post_at: string | null
  seo_strength: SeoStrength | null
  seo_checked_at: string | null
  facts_fetched_at: string | null
  researched_at: string | null
  /**
   * Approved, but the research agent parked it instead of drafting (no true
   * opening line, a draft that kept failing its fact checks, or the same error
   * pass after pass). Research leaves it alone until someone skips it or sends
   * it back (resume_research).
   */
  research_held: boolean
  research_held_at: string | null
  research_hold_reason: string | null
  /** What the agent saw: the writer's why-not, the failed checks, or the errors, one per line. */
  research_hold_detail: string | null
  /** What Skip offers first, as the API decides it: the hold reason, "Strong SEO already", or null. */
  suggested_skip_reason: string | null
  draft_stale: boolean
  founder_name: string | null
  founder_title: string | null
  founder_linkedin_url: string | null
  /** The founder's X profile (https://x.com/handle): from YC, or a profile the pipeline checked names them and the company. */
  founder_x_url: string | null
  founder_email: string | null
  /** The other people worth reaching at the company (co-founders, marketing or growth leads); [] when none were found. */
  people: OutreachPerson[]
  email_subject: string | null
  email_body: string | null
  linkedin_message: string | null
  checked_facts: string[]
  research_url: string | null
  /** Where each fact came from, keyed by field (raised_on, team_size, organic_keywords…). */
  fact_sources: Record<string, FactSource>
  /** Every place the pipeline found the company, in the order it found them (missing from an API before sources). */
  sources?: ProspectSource[]
  /**
   * Open YC job posts for a growth, marketing, content or SEO role, as the
   * pipeline last found them ([] when none, or never checked).
   */
  hiring: HiringPost[]
  /** When the YC jobs page was last checked; null = never. */
  hiring_checked_at: string | null
  stage: OutreachStage
  skip_reason: string | null
  approved_by: OutreachUserRef | null
  approved_at: string | null
  drafted_at: string | null
  /** Stage scheduled: when Forge sends the first email from Gmail. */
  send_scheduled_for: string | null
  /** Why the last send failed (the prospect went back to drafted); cleared by the next approval. */
  send_error: string | null
  sent_at: string | null
  /** Set once Forge sent the first email: replies and follow-ups go in this Gmail thread. */
  gmail_thread_id: string | null
  last_inbound_at: string | null
  last_outbound_at: string | null
  bounced_at: string | null
  /** A reply newer than our last email, or a bounce, that nobody has dealt with (clear_inbox). */
  needs_attention: boolean
  linkedin_invited_at: string | null
  linkedin_accepted_at: string | null
  linkedin_followup_sent_at: string | null
  replied_at: string | null
  reply_channel: ReplyChannel | null
  reply_text: string | null
  note_for_justin: string | null
  handed_to_justin_at: string | null
  handoff: Handoff | null
  client_id: number | null
  client: { id: number; slug: string; name: string; status: string } | null
  queued_for: string | null
  /** Set by the pipeline for backlog rows: higher is better (hiring, raise window, batch freshness). */
  backlog_score?: number | null
  /** When it was pulled from the backlog into To review. */
  pulled_at?: string | null
  external_ref: string | null
  created_by: OutreachUserRef | null
  created_at: string | null
  updated_at: string | null
  /** What the current stage allows, as the API decides it. */
  actions: OutreachAction[]
  /** Only on GET /outreach/prospects/{id} and action responses. */
  events?: OutreachEvent[]
  /** Detail only: every query behind the facts and the draft, oldest first. */
  research_log?: OutreachQuery[]
  /** Detail only: the pipeline runs that touched it, oldest first (the first candidates run found it). */
  runs?: OutreachRunRef[]
  /** Detail only: every email in its thread, oldest first. */
  messages?: OutreachMessage[]
}

export type MessageStatus = "scheduled" | "sending" | "sent" | "canceled" | "failed" | "received"

/**
 * One email in a prospect's thread. Out: `first` (the cold email) or `thread`
 * (a reply or follow-up), scheduled by a person and sent by Forge, or written
 * in Gmail itself (source gmail). In: the founder's `reply`, an `auto_reply`
 * or a `bounce`, found by the Gmail sync.
 */
export interface OutreachMessage {
  id: number
  direction: "out" | "in"
  kind: "first" | "thread" | "reply" | "auto_reply" | "bounce"
  status: MessageStatus
  source: "forge" | "gmail"
  from_email: string | null
  from_name: string | null
  to_email: string | null
  subject: string | null
  body: string | null
  /** In only: what they wrote, quoted history dropped. */
  reply_text: string | null
  scheduled_for: string | null
  /** Sent or received. */
  at: string | null
  error: string | null
  created_by: OutreachUserRef | null
  canceled_at: string | null
  created_at: string | null
}

/** GET /outreach/gmail: the sender's mailbox and what is waiting on it. */
export interface GmailStatus {
  sender_email: string
  connected: boolean
  email: string | null
  connected_at: string | null
  connected_by: OutreachUserRef | null
  scopes_ok: boolean
  missing_scopes: string[]
  synced_at: string | null
  /** A failure a person has to fix (revoked access): sending and the reply sync stop until reconnected. */
  error: string | null
  error_at: string | null
  can_send: boolean
  unavailable_reason: string | null
  paused: boolean
  scheduled_count: number
  next_send_at: string | null
  sent_today: number
}

/** One open YC job post for a growth/marketing/content/SEO role. */
export interface HiringPost {
  title: string
  url: string
  /** YC's role field: "marketing", "sales", … */
  role: string | null
  /** YC's relative age as of checked_at, e.g. "about 2 months". */
  posted: string | null
  /** YC's "last active", e.g. "21 days". */
  last_active: string | null
  /** SEO/GEO words found in the description ("SEO", "GEO", "search engine"…). */
  seo_terms: string[]
  /** The description asks the hire to own SEO, GEO or AI search. */
  mentions_seo: boolean
  checked_at: string | null
  /** YC's salary range, normalized by the pipeline: "$75K–$110K". Context for the team only, never in the email. */
  salary?: string | null
  /** YC's equity range when set: "0.5%–1%". */
  equity?: string | null
}

/** The queue's Hiring filter: any matching role, or one that mentions SEO/GEO. */
export type HiringFilter = "any" | "seo"

/** Someone at the company besides the founder the email goes to, as the pipeline found them. */
export interface OutreachPerson {
  name: string
  title: string | null
  x_url: string | null
  linkedin_url: string | null
  /** Where the person (and their X) came from: "YC company page, founders", a web search page… */
  source: string | null
  source_url: string | null
}

/** "@handle" for an x.com / twitter.com profile URL, or null. */
export function xHandle(url: string | null | undefined): string | null {
  const m = url?.match(/^https?:\/\/(?:www\.|mobile\.)?(?:x|twitter)\.com\/([A-Za-z0-9_]{1,15})/)
  return m ? `@${m[1]}` : null
}

export interface FactSource {
  source: string
  url?: string
  at?: string
}

/** One query the pipeline made: the shape of a run's queries and a prospect's research log. */
export interface OutreachQuery {
  tool: string
  query: string | null
  target: string | null
  /** A short summary of the answer; an object result arrives as its JSON text. */
  result: string | null
  cost_usd: number | null
  at: string | null
  run_id?: number
}

/** Where the pipeline found a company: the YC directory, an SEC Form D filing, a funding story, an accelerator portfolio. */
export interface ProspectSource {
  source: string
  ref?: string
  /** The YC page, the EDGAR filing, the article, the portfolio page. */
  url?: string
  /** "YC Spring 2026", "Form D, first sale 2026-07-01", "TechCrunch, 2026-10-02". */
  label?: string
  seen_at?: string
}

export type RunKind = "candidates" | "research" | "sources"
export type RunTrigger = "manual" | "schedule"
export type RunStatus = "running" | "ok" | "partial" | "failed"

export interface OutreachRunRef {
  id: number
  kind: RunKind
  trigger: RunTrigger
  status: RunStatus
  started_at: string | null
}

export interface RunStats {
  considered?: number
  added?: number
  skipped_existing?: number
  /** Why candidates were dropped: { reason: count }. */
  filtered?: Record<string, number>
  researched?: number
  drafted?: number
  /** Candidates runs since the backlog: new companies stored in the backlog this run. */
  added_to_backlog?: number
  /** Pulled from the backlog into To review this run. */
  queued_for_review?: number
  /** Hand-pulled candidates whose facts were refreshed. */
  refreshed?: number
  /** Skipped by the run: strong SEO, raise window passed, aged out of the new-YC window, site down. */
  skipped_from_backlog?: number
  /** The backlog's size after the run. */
  backlog_size?: number
  /** Per source: what was found, filtered (by reason), merged into a company already known, and added. */
  sources?: Record<string, SourceRunStats>
  [key: string]: unknown
}

/** One source's numbers in a run (candidates or sources). Every field is optional: each run reports what it knows. */
export interface SourceRunStats {
  found?: number
  filtered?: Record<string, number>
  /** Matched a company already in Forge or found in another source this run (merged, never duplicated). */
  duplicates?: number
  added_to_backlog?: number
  /** Form D: domain resolution (guess, search, then a claude check against the filing). */
  resolution?: { attempted?: number; resolved?: number; unresolved?: number; rate?: number | null }
  [key: string]: unknown
}

export interface OutreachRun extends OutreachRunRef {
  finished_at: string | null
  duration_seconds: number | null
  stats: RunStats
  cost_usd: number
  query_count: number
  error_count: number
  prospect_count: number
  created_by: OutreachUserRef | null
  created_at: string | null
  updated_at: string | null
}

export type RunError = string | { message?: string; [key: string]: unknown }

export interface OutreachRunDetail extends OutreachRun {
  queries: OutreachQuery[]
  errors: RunError[]
  settings_used: Record<string, unknown>
  prospect_refs: string[]
  unresolved_refs: string[]
  prospects: { id: number; company: string; domain: string | null; stage: OutreachStage; external_ref: string | null }[]
}

export interface RunsSummary {
  pipeline_enabled: boolean
  timezone: string
  running: number
  last: Record<RunKind, OutreachRun | null>
  next: Record<RunKind, string | null>
}

export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun"
export type SourceKey = "yc_new" | "yc_established" | "google_news" | "form_d" | "news_feeds" | "accelerators"

export interface ChecklistItem {
  key: string
  label: string
  detail: string | null
  done: boolean
  done_at: string | null
  done_by: OutreachUserRef | null
}

/** What PUT /outreach/settings takes; GET adds the read-only fields in OutreachSettings. */
export interface OutreachSettingsValues {
  pipeline_enabled: boolean
  daily_target: number
  split: { just_raised: number; new_yc: number }
  schedule: { days: Weekday[]; time: string }
  research_interval_minutes: number
  raise_window_too_early_days: number
  raise_window_late_days: number
  strong_seo_keywords: number
  sources: Record<SourceKey, boolean>
  excluded_industries: string[]
  /** Phrases that rule a company out when its one-liner says them. */
  excluded_keywords: string[]
  /** The same, anywhere in the long description (only phrases that are never incidental). */
  excluded_description_keywords: string[]
  ankur_lines: string[]
  signature: string
  sender_email: string
  sender_name: string
  /** Stops the send job; approved emails keep their place and wait. */
  sending_paused: boolean
  /** When first emails may leave: days, start and end in the window's own time zone; `hours` gives a day its own. */
  send_window: SendWindow
  /** The weekly ramp: from each step's date To review is topped up to its target instead of daily_target. */
  ramp_enabled?: boolean
  ramp?: RampStep[]
  /** At least this long between approving and sending (60 or more). */
  send_delay_minutes: number
  send_gap_minutes: number
  daily_send_cap: number
  /** The undo window on replies and follow-ups. */
  reply_delay_minutes: number
  /** New YC: batches whose Demo Day was 45 days to this many days ago (default 365). */
  new_yc_max_days: number
  /** Fresh Google News raise lookups per candidates run (free; cached 14 days). */
  news_lookups_max: number
}

export interface SendWindow {
  days: Weekday[]
  start: string
  end: string
  timezone: string
  /** A day's own hours ({ mon: { start: "12:00", end: "17:00" } }); the others use start and end. */
  hours?: Partial<Record<Weekday, { start: string; end: string }>>
}

export interface RampStep {
  /** Y-m-d, team time zone. */
  from: string
  daily_target: number
}

/** The ramp as it stands today (GET /outreach/settings): what the morning job uses. */
export interface RampStatus {
  enabled: boolean
  current: RampStep | null
  next: RampStep | null
  daily_target: number
  split: { just_raised: number; new_yc: number }
}

export interface OutreachSettings extends OutreachSettingsValues {
  /** Missing from an API before the ramp. */
  ramp_status?: RampStatus
  checklist: ChecklistItem[]
  open_items: number
  timezone: string
  updated_at: string | null
  updated_by: OutreachUserRef | null
}

export interface OutreachStats {
  date: string
  timezone: string
  sent_this_week: number
  sent_total: number
  replies_this_week: number
  replies_total: number
  /** Percent of everything sent that got a reply, one decimal; null before the first send. */
  reply_rate: number | null
  waiting_on_justin: number
  with_justin: number
  stages: Record<OutreachStage, number>
  groups: Record<TodayGroupKey, number>
  tabs: Record<OutreachTab, number>
  /** Missing from an API before the backlog. */
  backlog?: BacklogStats
}

/** Every eligible company the candidates job found and has not put up for review yet. */
export interface BacklogStats {
  total: number
  by_list: Record<OutreachList, number>
  /** Newest batch first; a null batch is the July funding-news leads. */
  by_batch: { yc_batch: string | null; list_type: OutreachList; count: number }[]
  /** Where the backlog's companies were found ({ yc: 300, form_d: 120 }); one in several sources counts under each. */
  by_source?: Record<string, number>
  /** Found in more than one source (merged into one prospect). */
  multi_source?: number
  daily_target: number
  split: Record<OutreachList, number>
  /** total / daily_target, one decimal; null without a target. */
  days_of_supply: number | null
  days_of_supply_by_list: Record<OutreachList, number | null>
  oldest_facts_at: string | null
  newest_added_at: string | null
}

export type TodayGroups = Record<TodayGroupKey, OutreachProspect[]>

/* ------------------------------------------------------------------------ */
/* Access                                                                    */
/* ------------------------------------------------------------------------ */

/** Flag-only: being an admin does not open Outreach. */
export function canUseOutreach(user: { outreach_enabled?: boolean } | null | undefined): boolean {
  return user?.outreach_enabled === true
}

/* ------------------------------------------------------------------------ */
/* Stages and tabs                                                           */
/* ------------------------------------------------------------------------ */

/** a9-strength dots, the same steps Creator Studio uses. */
export const DOT: Record<StatusTone, string> = {
  error: "bg-[#db0007b7] dark:bg-[#fe4e54e4]",
  warning: "bg-[#ffc53d] dark:bg-[#ffc53d]",
  info: "bg-[#0090ff] dark:bg-[#0090ff]",
  success: "bg-[#008f4acf] dark:bg-[#44ffa49e]",
  violet: "bg-[#6e56cf] dark:bg-[#6e56cf]",
  neutral: "bg-[#989898] dark:bg-[#6e7679]",
}

export const STAGE: Record<OutreachStage, { label: string; tone: StatusTone }> = {
  candidate: { label: "To review", tone: "info" },
  approved: { label: "Researching", tone: "neutral" },
  drafted: { label: "Drafted", tone: "warning" },
  scheduled: { label: "Scheduled", tone: "info" },
  sent: { label: "Sent", tone: "info" },
  linkedin_invited: { label: "Invite sent", tone: "violet" },
  linkedin_accepted: { label: "Connected", tone: "violet" },
  replied: { label: "Replied", tone: "success" },
  skipped: { label: "Skipped", tone: "neutral" },
  backlog: { label: "Backlog", tone: "neutral" },
}

export const TABS: { value: OutreachTab; label: string; tone?: StatusTone }[] = [
  { value: "today", label: "Today" },
  { value: "inbox", label: "Inbox", tone: "success" },
  { value: "candidate", label: "To review", tone: "info" },
  { value: "approved", label: "Researching", tone: "neutral" },
  { value: "drafted", label: "Drafted", tone: "warning" },
  { value: "scheduled", label: "Scheduled", tone: "info" },
  { value: "sent", label: "Sent", tone: "info" },
  { value: "linkedin", label: "LinkedIn", tone: "violet" },
  { value: "replied", label: "Replied", tone: "success" },
  { value: "skipped", label: "Skipped", tone: "neutral" },
  { value: "backlog", label: "Backlog", tone: "neutral" },
]

/** A tab that is one stage (or both LinkedIn stages): everything but Today and Inbox. */
export type StageTab = Exclude<OutreachTab, "today" | "inbox">

/** One row of the Inbox: a Gmail thread Forge started, by prospect (GET /outreach/inbox). */
export interface InboxThread {
  prospect: OutreachProspect
  /** A reply or bounce nobody has dealt with (Mark done, or writing back, reads it). */
  unread: boolean
  subject: string | null
  message_count: number
  /** An email of ours is waiting to go in this thread. */
  has_scheduled: boolean
  last: {
    direction: "in" | "out"
    kind: OutreachMessage["kind"]
    status: OutreachMessage["status"]
    from_name: string | null
    from_email: string | null
    snippet: string
    at: string | null
  } | null
}

export type InboxView = "all" | "unread"

/** Who the latest message is from, as Gmail's list shows it: "Ada", "You", or the address. */
export function inboxSender(t: InboxThread) {
  const last = t.last
  if (!last) return t.prospect.founder_name ?? t.prospect.company
  if (last.direction === "out") return "You"
  if (last.kind === "bounce") return "Mail delivery"
  return last.from_name?.split(" ")[0] || last.from_email || t.prospect.founder_name || t.prospect.company
}

export function isTab(value: string | null | undefined): value is OutreachTab {
  return TABS.some((t) => t.value === value)
}

export function tabOf(stage: OutreachStage): StageTab {
  return stage === "linkedin_invited" || stage === "linkedin_accepted" ? "linkedin" : stage
}

export function isLinkedInStage(stage: OutreachStage) {
  return stage === "linkedin_invited" || stage === "linkedin_accepted"
}

export function stageLabel(p: Pick<OutreachProspect, "stage">) {
  return STAGE[p.stage].label
}

/** The stepper in the panel: Drafted → Sent → LinkedIn → Replied. */
export const PIPELINE = ["drafted", "sent", "linkedin", "replied"] as const
export type PipelineStep = (typeof PIPELINE)[number]

/** How far along the stepper a stage is; -1 before a draft or once skipped. */
export function pipelineIndex(stage: OutreachStage): number {
  if (stage === "drafted" || stage === "scheduled") return 0
  if (stage === "sent") return 1
  if (isLinkedInStage(stage)) return 2
  if (stage === "replied") return 3
  return -1
}

export const LIST_TYPE: Record<OutreachList, { label: string; tone: StatusTone; help: string }> = {
  just_raised: { label: "Just raised", tone: "info", help: "An established YC company that announced a round in the last six months." },
  new_yc: { label: "New YC", tone: "violet", help: "A company from a recent YC batch (45 days to a year past Demo Day by default)." },
}

export const WINDOW: Record<Exclude<RaiseWindow, "unknown">, { label: string; tone: StatusTone }> = {
  too_early: { label: "Too early", tone: "warning" },
  good: { label: "Good window", tone: "success" },
  late: { label: "Late", tone: "neutral" },
}

/** The raise-window thresholds from Outreach settings, in days since the raise. */
export interface WindowThresholds {
  too_early: number
  late: number
}

export function windowThresholds(settings: Pick<OutreachSettingsValues, "raise_window_too_early_days" | "raise_window_late_days"> | undefined): WindowThresholds | null {
  return settings ? { too_early: settings.raise_window_too_early_days, late: settings.raise_window_late_days } : null
}

/**
 * A window badge's explanation, with the numbers from settings (the API
 * classifies by the same ones). Without settings yet, the sentence leaves the
 * numbers out rather than guessing them.
 */
export function windowHelp(window: Exclude<RaiseWindow, "unknown">, t: WindowThresholds | null): string {
  switch (window) {
    case "too_early":
      return t ? `Under ${t.too_early} days since the raise: every agency is writing this week.` : "Too soon after the raise: every agency is writing this week."
    case "good":
      return t ? `${t.too_early} to ${t.late} days since the raise.` : "In the window after the raise."
    case "late":
      return t ? `Over ${t.late} days since the raise.` : "Long after the raise."
  }
}

export const SEO: Record<SeoStrength, { label: string; tone: StatusTone }> = {
  weak: { label: "Weak SEO", tone: "success" },
  some: { label: "Some SEO", tone: "neutral" },
  strong: { label: "Strong SEO", tone: "error" },
}

export const SKIP_REASONS = [
  "Strong SEO already",
  "Has a content team",
  "Hardware or biotech",
  "Outside the US",
  "Too big",
  "Site down",
  "No real SEO gap",
  "Already talking to an agency",
  "Founder already contacted",
  "Couldn't verify the email",
] as const

/**
 * The reason Skip offers first, as the API suggests it (a held prospect's hold
 * reason, else "Strong SEO already" for a strong-SEO site). A row cached before
 * the API sent the field falls back to the strong-SEO rule.
 */
export function defaultSkipReason(p: Pick<OutreachProspect, "seo_strength"> & Partial<Pick<OutreachProspect, "suggested_skip_reason">>): string | null {
  if (p.suggested_skip_reason !== undefined) return p.suggested_skip_reason
  return p.seo_strength === "strong" ? "Strong SEO already" : null
}

/** The Skip menu's reasons: the suggested one first (once), then the usual list. */
export function skipOptions(p: Parameters<typeof defaultSkipReason>[0]): { reasons: string[]; suggested: string | null } {
  const suggested = defaultSkipReason(p)
  return { reasons: suggested ? [suggested, ...SKIP_REASONS.filter((r) => r !== suggested)] : [...SKIP_REASONS], suggested }
}

/* ------------------------------------------------------------------------ */
/* Research holds                                                            */
/* ------------------------------------------------------------------------ */

/** The research agent's hold reasons (outreach_research.py); a repeated error reads "Research failed 3 times: …". */
export const HOLD_NO_OPENING_LINE = "Couldn't find a true opening line"
export const HOLD_FAILED_CHECKS = "Couldn't write a draft that passes the fact checks"

/** Approved and parked by the research agent: waiting on a person, not on research. */
export function isHeld(p: Pick<OutreachProspect, "stage"> & Partial<Pick<OutreachProspect, "research_held">>): boolean {
  return p.stage === "approved" && p.research_held === true
}

/** What kind of hold, for the detail's heading and icon. */
export function holdKind(reason: string | null | undefined): "no_opening_line" | "failed_checks" | "errors" | "other" {
  if (!reason) return "other"
  if (reason === HOLD_NO_OPENING_LINE) return "no_opening_line"
  if (reason === HOLD_FAILED_CHECKS) return "failed_checks"
  if (/^Research failed \d+ times/.test(reason)) return "errors"
  return "other"
}

/**
 * The hold's detail as lines: the failed checks (the agent joins them with
 * "; "), the errors (one per run, one per line), or the writer's why-not.
 */
export function holdDetailLines(p: Pick<OutreachProspect, "research_hold_reason" | "research_hold_detail">): string[] {
  const detail = (p.research_hold_detail ?? "").trim()
  if (!detail) return []
  const lines = detail.split(/\n+/).map((l) => l.trim()).filter(Boolean)
  if (lines.length === 1 && holdKind(p.research_hold_reason) === "failed_checks") {
    return lines[0].split(/;\s+/).map((l) => l.trim()).filter(Boolean)
  }
  return lines
}

/** The heading over a hold's detail. */
export function holdDetailLabel(reason: string | null | undefined): string {
  switch (holdKind(reason)) {
    case "failed_checks":
      return "Checks the draft failed"
    case "errors":
      return "Each failed pass"
    default:
      return "What the agent saw"
  }
}

/* ------------------------------------------------------------------------ */
/* Today                                                                     */
/* ------------------------------------------------------------------------ */

export const TODAY_GROUPS: { key: TodayGroupKey; title: string; hint: string; tone: StatusTone }[] = [
  { key: "inbox", title: "Inbox", hint: "Replies and bounces in the threads Forge started. Answer from the panel, hand it to Justin, or mark it done.", tone: "success" },
  { key: "review", title: "To review", hint: "Approve the ones worth an email, skip the rest. Approved ones are researched and drafted within the hour.", tone: "info" },
  { key: "researching", title: "Researching", hint: "Approved. The research agent checks the site and drafts the email; it lands in Ready to send.", tone: "neutral" },
  { key: "send", title: "Ready to send", hint: "Read each draft and approve the send: Forge sends it from justin@snowballrank.com an hour later at the soonest, inside the sending window.", tone: "warning" },
  { key: "scheduled", title: "Scheduled", hint: "Approved and waiting for their time. Cancel any of them until it goes.", tone: "info" },
  { key: "invite", title: "LinkedIn invite due", hint: "Emailed before today. Send a connection invite from Justin's account, no note.", tone: "info" },
  { key: "followup", title: "Connected, send the follow-up", hint: "They accepted. Send the LinkedIn follow-up message from the draft.", tone: "violet" },
]

/** Calendar day in local time, "2026-10-12", for comparing timestamps to "today". */
export function localDay(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

/**
 * Which Today group a prospect belongs in, by the API's rules (OutreachQueue);
 * used to move a row between groups the moment an action is taken.
 */
export function todayGroupOf(p: OutreachProspect, today: string): TodayGroupKey | null {
  // The API can list a bounced prospect under invite too; the inbox is where it needs a person first.
  if (p.needs_attention) return "inbox"
  switch (p.stage) {
    case "candidate":
      return !p.queued_for || p.queued_for <= today ? "review" : null
    case "approved":
      return "researching"
    case "drafted":
      return "send"
    case "scheduled":
      return "scheduled"
    case "sent":
      return !p.linkedin_invited_at && p.sent_at && localDay(new Date(p.sent_at)) < today ? "invite" : null
    case "linkedin_accepted":
      return p.linkedin_followup_sent_at ? null : "followup"
    default:
      return null
  }
}

/** Put an updated prospect where it now belongs (or nowhere), keeping each group's order. */
export function regroupToday(groups: TodayGroups, updated: OutreachProspect, today: string): TodayGroups {
  const target = todayGroupOf(updated, today)
  const next = {} as TodayGroups
  for (const key of Object.keys(groups) as TodayGroupKey[]) {
    const rows = groups[key]
    const at = rows.findIndex((r) => r.id === updated.id)
    if (key === target) {
      next[key] = at >= 0 ? rows.map((r) => (r.id === updated.id ? updated : r)) : [...rows, updated]
    } else {
      next[key] = at >= 0 ? rows.filter((r) => r.id !== updated.id) : rows
    }
  }
  return next
}

/** What an action does to a prospect, applied locally before the server answers. */
export function applyActionLocally(
  p: OutreachProspect,
  action: OutreachAction,
  body: { reason?: string; channel?: ReplyChannel; text?: string; note?: string } = {},
  now: Date = new Date(),
): OutreachProspect {
  const at = now.toISOString()
  switch (action) {
    case "approve":
      return { ...p, stage: "approved", approved_at: at, actions: ["skip"] }
    case "resume_research":
      return {
        ...p,
        research_held: false,
        research_held_at: null,
        research_hold_reason: null,
        research_hold_detail: null,
        suggested_skip_reason: p.seo_strength === "strong" ? "Strong SEO already" : null,
        actions: p.actions.filter((a) => a !== "resume_research"),
      }
    case "schedule_send":
      return { ...p, stage: "scheduled", send_error: null, actions: ["cancel_send", "skip"] }
    case "cancel_send":
      return { ...p, stage: "drafted", send_scheduled_for: null, actions: ["schedule_send", "mark_sent", "skip"] }
    case "clear_inbox":
      return { ...p, needs_attention: false, actions: p.actions.filter((a) => a !== "clear_inbox") }
    case "mark_sent":
      return { ...p, stage: "sent", sent_at: at, draft_stale: false, actions: ["linkedin_invited", "replied", "skip"] }
    case "linkedin_invited":
      return { ...p, stage: "linkedin_invited", linkedin_invited_at: at, actions: ["linkedin_accepted", "replied", "skip"] }
    case "linkedin_accepted":
      return { ...p, stage: "linkedin_accepted", linkedin_accepted_at: at, actions: ["followup_sent", "replied", "skip"] }
    case "followup_sent":
      return { ...p, linkedin_followup_sent_at: at, actions: p.actions.filter((a) => a !== "followup_sent") }
    case "replied":
      return { ...p, stage: "replied", replied_at: at, reply_channel: body.channel ?? "email", reply_text: body.text ?? null, handoff: "waiting", actions: ["hand_to_justin", "convert_to_prospect"] }
    case "skip":
      return { ...p, stage: "skipped", skip_reason: body.reason ?? null, actions: ["reopen"] }
    case "pull":
      return { ...p, stage: "candidate", pulled_at: at, queued_for: localDay(now), actions: ["approve", "skip", "back_to_backlog"] }
    case "back_to_backlog":
      return { ...p, stage: "backlog", pulled_at: null, queued_for: null, actions: ["skip", "pull"] }
    case "hand_to_justin":
      return { ...p, handed_to_justin_at: at, handoff: "with_justin", note_for_justin: body.note ?? p.note_for_justin, actions: ["convert_to_prospect"] }
    default:
      return p
  }
}

/* ------------------------------------------------------------------------ */
/* Header numbers                                                            */
/* ------------------------------------------------------------------------ */

export function formatRate(rate: number | null | undefined) {
  if (rate === null || rate === undefined) return "–"
  return `${Number.isInteger(rate) ? rate : rate.toFixed(1)}%`
}

export function statsDisplay(stats: OutreachStats | undefined) {
  return {
    sent: stats?.sent_this_week ?? 0,
    replies: stats?.replies_total ?? 0,
    rate: formatRate(stats?.reply_rate),
    waiting: stats?.waiting_on_justin ?? 0,
  }
}

/* ------------------------------------------------------------------------ */
/* Dates                                                                     */
/* ------------------------------------------------------------------------ */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

/** "Y-m-d" (a date column) or ISO (a timestamp) → Date; date-only reads as local midnight. */
function parse(value: string): Date {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value)
}

function daysBetween(a: Date, b: Date) {
  const day = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  return Math.round((day(b) - day(a)) / 86_400_000)
}

/** "7:02am" */
export function clock(d: Date) {
  const h = d.getHours()
  return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")}${h < 12 ? "am" : "pm"}`
}

/** "May 12", with the year when it is not this year: "May 12, 2025". */
export function shortDate(value: string, now: Date = new Date()) {
  const d = parse(value)
  return `${MONTHS[d.getMonth()]} ${d.getDate()}${d.getFullYear() !== now.getFullYear() ? `, ${d.getFullYear()}` : ""}`
}

/** A full date (and time, for a timestamp) for a hover title: "Mon, Oct 12, 2026, 7:02am". */
export function absolute(value: string) {
  const d = parse(value)
  const time = /^\d{4}-\d{2}-\d{2}$/.test(value) ? "" : `, ${clock(d)}`
  return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}${time}`
}

/** When something happened, as a timeline says it: "7:02am", "yesterday 10:12am", "Fri 3:02pm", "Oct 3". */
export function when(value: string, now: Date = new Date()) {
  const d = parse(value)
  const days = daysBetween(d, now)
  if (days <= 0) return clock(d)
  if (days === 1) return `yesterday ${clock(d)}`
  if (days < 7) return `${WEEKDAYS[d.getDay()]} ${clock(d)}`
  return shortDate(value, now)
}

/** How long ago, briefly: "just now", "52 min ago", "3h ago", "yesterday", "Fri", "Oct 3". */
export function ago(value: string, now: Date = new Date()) {
  const d = parse(value)
  const minutes = Math.floor((now.getTime() - d.getTime()) / 60_000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes} min ago`
  const days = daysBetween(d, now)
  if (days === 0) return `${Math.floor(minutes / 60)}h ago`
  if (days === 1) return "yesterday"
  if (days < 7) return WEEKDAYS[d.getDay()]
  return shortDate(value, now)
}

/** The one line under a row's stage pill. */
export function stageNote(p: OutreachProspect, now: Date = new Date()): string {
  switch (p.stage) {
    case "candidate":
      // A backlog pull keeps the row's created_at, so the pull is when it was queued.
      return p.pulled_at || p.created_at ? `Queued ${when((p.pulled_at || p.created_at)!, now)}` : "Waiting for review"
    case "approved":
      if (isHeld(p)) return p.research_hold_reason ?? "Research held"
      return p.approved_at ? `Approved ${when(p.approved_at, now)}` : "Approved"
    case "drafted":
      if (p.send_error) return "Send failed"
      return p.drafted_at ? `Ready since ${when(p.drafted_at, now)}` : "Ready"
    case "scheduled":
      return p.send_scheduled_for ? `Sends ${upcoming(p.send_scheduled_for, now)}` : "Scheduled"
    case "sent":
      if (p.bounced_at) return "Bounced"
      return p.sent_at ? `Emailed ${when(p.sent_at, now)}` : "Emailed"
    case "linkedin_invited":
      return p.linkedin_invited_at ? `Invite sent ${ago(p.linkedin_invited_at, now)}` : "Invite sent"
    case "linkedin_accepted":
      if (p.linkedin_followup_sent_at) return `Follow-up sent ${ago(p.linkedin_followup_sent_at, now)}`
      return p.linkedin_accepted_at ? `Accepted ${when(p.linkedin_accepted_at, now)}` : "Accepted"
    case "replied":
      return p.replied_at ? `Replied ${ago(p.replied_at, now)}` : "Replied"
    case "skipped":
      return p.skip_reason ?? "Skipped"
    case "backlog":
      return p.created_at ? `In the backlog since ${shortDate(p.created_at, now)}` : "In the backlog"
  }
}

/** "Info from Oct 3" once the facts are more than a week old; null while fresh. */
export function staleInfo(p: Pick<OutreachProspect, "facts_fetched_at">, now: Date = new Date(), days = 7): string | null {
  if (!p.facts_fetched_at) return null
  return daysBetween(parse(p.facts_fetched_at), now) > days ? `Info from ${shortDate(p.facts_fetched_at, now)}` : null
}

/* ------------------------------------------------------------------------ */
/* Company facts                                                             */
/* ------------------------------------------------------------------------ */

const SEASON: Record<string, string> = { winter: "W", spring: "X", summer: "S", fall: "F" }

/** "Winter 2024" → "W24"; anything else unchanged. */
export function batchShort(batch: string | null | undefined): string | null {
  if (!batch) return null
  const m = /^(winter|spring|summer|fall)\s+(\d{4})$/i.exec(batch.trim())
  return m ? `${SEASON[m[1].toLowerCase()]}${m[2].slice(2)}` : batch
}

/** "$20M Series A", "$20M", "Series A", or null. */
export function raiseLabel(p: Pick<OutreachProspect, "raise_amount" | "raise_round">): string | null {
  return [p.raise_amount, p.raise_round].filter(Boolean).join(" ") || null
}

/**
 * Row line two in three segments, so a row can let the raise label (the long
 * part) truncate while the date and days stay readable:
 * head "Founded 2023 · YC W24", raise "Raised $20M Series A", tail "May 12 · 153d ago".
 */
export function factsSegments(p: OutreachProspect, now: Date = new Date()): { head: string[]; raise: string | null; tail: string[] } {
  const head: string[] = []
  if (p.founded_year) head.push(`Founded ${p.founded_year}`)
  const batch = batchShort(p.yc_batch)
  if (batch) head.push(`YC ${batch}`)
  const label = raiseLabel(p)
  const tail: string[] = []
  if (p.raised_on) {
    tail.push(shortDate(p.raised_on, now))
    if (p.days_since_raise !== null) tail.push(`${p.days_since_raise}d ago`)
  } else if (!label && p.team_size) {
    tail.push(`${p.team_size} ${p.team_size === 1 ? "person" : "people"}`)
  }
  return { head, raise: label ? `Raised ${label}` : null, tail }
}

/** The same line as plain parts: "Founded 2023 · YC W24 · Raised $20M Series A · May 12 · 153d ago". */
export function factsLine(p: OutreachProspect, now: Date = new Date()): string[] {
  const { head, raise, tail } = factsSegments(p, now)
  return [...head, ...(raise ? [raise] : []), ...tail]
}

/** "$120", "$3.1k", "$1.2M" */
export function money(value: number) {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1).replace(/\.0$/, "")}k`
  return `$${Math.round(value)}`
}

/* ------------------------------------------------------------------------ */
/* Hiring                                                                    */
/* ------------------------------------------------------------------------ */

/** YC titles come with stray spaces ("Growth Marketer  (Founding)"). */
export function jobTitle(title: string): string {
  return title.replace(/\s+/g, " ").trim()
}

/** The posts, the SEO/GEO ones first (the badge and the sheet lead with them). */
export function hiringPosts(p: Partial<Pick<OutreachProspect, "hiring">>): HiringPost[] {
  const posts = p.hiring ?? []
  return [...posts.filter((h) => h.mentions_seo), ...posts.filter((h) => !h.mentions_seo)]
}

/**
 * The row badge: "Hiring for SEO/GEO" when a post asks the hire to own SEO or
 * GEO (the strong variant), else "Hiring: Growth Marketer" (+N for more);
 * null with no matching post.
 */
export function hiringBadge(p: Partial<Pick<OutreachProspect, "hiring">>): { label: string; seo: boolean; title: string } | null {
  const posts = hiringPosts(p)
  if (!posts.length) return null
  const seo = posts[0].mentions_seo
  const titles = posts.map((h) => jobTitle(h.title))
  const more = posts.length > 1 ? ` +${posts.length - 1}` : ""
  return {
    label: seo ? "Hiring for SEO/GEO" : `Hiring: ${titles[0]}${more}`,
    seo,
    // The hover names each post with its pay, as the sheet does.
    title: `Open YC job post${posts.length > 1 ? "s" : ""}: ${posts.map(hiringTitleWithPay).join(", ")}`,
  }
}

/** "posted about 2 months ago · active 21 days ago" (YC's words, as of the check). */
export function hiringAge(h: Pick<HiringPost, "posted" | "last_active">): string {
  return [h.posted && `posted ${h.posted} ago`, h.last_active && `active ${h.last_active} ago`].filter(Boolean).join(" · ")
}

/**
 * A post's pay as the team reads it: "$75K–$110K", plus "0.5%–1% equity" when
 * YC lists equity. Context for Alenah and Justin's call, never for the email.
 */
export function hiringPay(h: Pick<HiringPost, "salary" | "equity">): string | null {
  const parts = [h.salary?.trim(), h.equity?.trim() ? `${h.equity.trim()} equity` : null].filter(Boolean)
  return parts.length ? parts.join(" · ") : null
}

/** A post's title with its pay: "Growth Marketer (Founding) · $75K–$110K". */
export function hiringTitleWithPay(h: Pick<HiringPost, "title" | "salary" | "equity">): string {
  const pay = hiringPay(h)
  return pay ? `${jobTitle(h.title)} · ${pay}` : jobTitle(h.title)
}

/** The Hiring filter's options, in the order the menu shows them. */
export const HIRING_FILTERS: { value: HiringFilter; label: string }[] = [
  { value: "any", label: "Hiring marketing" },
  { value: "seo", label: "Hiring for SEO/GEO" },
]

/** The SEO badge's tooltip: "1,240 keywords · ~$3.1k/mo · 14 posts, last Sep 10". */
export function seoSummary(p: OutreachProspect, now: Date = new Date()): string | null {
  const parts: string[] = []
  if (p.organic_keywords !== null) parts.push(`${p.organic_keywords.toLocaleString("en-US")} keywords`)
  if (p.organic_etv !== null) parts.push(`~${money(p.organic_etv)}/mo`)
  if (p.blog_posts !== null) parts.push(`${p.blog_posts} ${p.blog_posts === 1 ? "post" : "posts"}${p.last_post_at ? `, last ${shortDate(p.last_post_at, now)}` : ""}`)
  return parts.length ? parts.join(" · ") : null
}

/* ------------------------------------------------------------------------ */
/* Timeline                                                                  */
/* ------------------------------------------------------------------------ */

const FIELD_LABEL: Record<string, string> = {
  email_subject: "the subject",
  email_body: "the email",
  linkedin_message: "the LinkedIn note",
  note_for_justin: "the note for Justin",
}

/** One event as a sentence fragment after the person's name. */
export function eventText(e: Pick<OutreachEvent, "type" | "meta">): string {
  const meta = e.meta ?? {}
  switch (e.type) {
    case "created":
      return "queued it"
    case "approve":
      return "approved it for research"
    case "drafted":
      return "drafted the email and LinkedIn note"
    case "mark_sent":
      return "sent the email"
    case "schedule_send":
      return typeof meta.scheduled_for === "string" ? `approved the send for ${upcoming(meta.scheduled_for)}` : "approved the send"
    case "cancel_send":
      return "canceled the send"
    case "email_sent":
      if (meta.source === "gmail") return "replied from Gmail"
      return meta.kind === "thread" ? "sent a reply from Gmail" : "sent the email from Gmail"
    case "email_scheduled":
      return "wrote a reply"
    case "email_canceled":
      return "canceled a reply"
    case "send_failed":
      return typeof meta.error === "string" ? `could not send: ${meta.error}` : "could not send the email"
    case "send_rescheduled":
      return typeof meta.to === "string" ? `moved the send to ${upcoming(meta.to)}` : "moved the send"
    case "email_received":
      return "brought in a new reply"
    case "auto_reply":
      return "brought in an auto-reply"
    case "bounced":
      return "reported the email bounced"
    case "clear_inbox":
      return "marked the reply dealt with"
    case "linkedin_invited":
      return "sent the LinkedIn invite"
    case "linkedin_accepted":
      return "marked the invite accepted"
    case "followup_sent":
      return "sent the LinkedIn follow-up"
    case "replied":
      if (meta.message_id) return "brought in their reply"
      return meta.channel === "linkedin" ? "logged a LinkedIn reply" : "logged an email reply"
    case "skip":
      return typeof meta.reason === "string" ? `skipped it: ${meta.reason}` : "skipped it"
    case "hand_to_justin":
      return "handed it to Justin"
    case "convert_to_prospect":
      return "converted it to a client prospect"
    case "reopen":
      return "reopened it"
    case "pull":
    case "pulled":
      return "pulled it from the backlog into To review"
    case "back_to_backlog":
      return "put it back in the backlog"
    case "hold_research":
      return typeof meta.reason === "string" ? `held its research: ${meta.reason}` : "held its research"
    case "resume_research":
      return "sent it back to research"
    case "edited": {
      const fields = Array.isArray(meta.fields) ? (meta.fields as string[]) : []
      const named = fields.map((f) => FIELD_LABEL[f]).filter(Boolean)
      return named.length ? `edited ${named.join(" and ")}` : "updated the details"
    }
    default:
      return e.type.replace(/_/g, " ")
  }
}

/** What Forge's own jobs log with no person behind them: the Gmail sync and the send job. */
const SYSTEM_EVENTS = new Set(["email_sent", "send_failed", "send_rescheduled", "email_received", "auto_reply", "bounced", "replied"])

function eventKind(e: Pick<OutreachEvent, "via" | "user" | "type">): "agent" | "person" | "system" {
  if (e.via === "token") return "agent"
  return !e.user && SYSTEM_EVENTS.has(e.type) ? "system" : "person"
}

/** Newest first, as the panel lists them. */
export function timeline(events: OutreachEvent[] | undefined, now: Date = new Date()) {
  return [...(events ?? [])]
    .sort((a, b) => b.id - a.id)
    .map((e) => ({
      key: e.id,
      who: e.user?.name ?? (eventKind(e) === "system" ? "Gmail" : "Someone"),
      kind: eventKind(e),
      what: eventText(e),
      at: when(e.created_at, now),
      title: absolute(e.created_at),
    }))
}

/* ------------------------------------------------------------------------ */
/* Runs                                                                      */
/* ------------------------------------------------------------------------ */

export const RUN_KIND: Record<RunKind, { label: string; help: string }> = {
  candidates: { label: "Candidates", help: "The morning pull: every eligible new YC company and recent raise goes to the backlog, and To review is topped up from it, best first." },
  research: { label: "Research", help: "A research pass: checks each approved prospect's site and drafts the email." },
  sources: { label: "Sources", help: "The nightly source pull: SEC Form D filings, funding news and accelerator portfolios, each company's website found and checked, ready for the morning run." },
}

export const RUN_STATUS: Record<RunStatus, { label: string; tone: StatusTone }> = {
  running: { label: "Running", tone: "info" },
  ok: { label: "OK", tone: "success" },
  partial: { label: "Partial", tone: "warning" },
  failed: { label: "Failed", tone: "error" },
}

export const RUN_TRIGGER: Record<RunTrigger, string> = {
  manual: "Manual",
  schedule: "Scheduled",
}

/** "4m 28s", "52s", "1h 3m"; "–" while running. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "–"
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  if (m < 60) return `${m}m ${seconds % 60}s`
  return `${Math.floor(m / 60)}h ${m % 60}m`
}

/** "$0.42", "$0.003", "$0" — small enough to show what a query cost. */
export function usd(value: number | null | undefined): string {
  if (value === null || value === undefined) return "–"
  if (value === 0) return "$0"
  if (value < 0.01) return `$${value.toFixed(3)}`
  return `$${value.toFixed(2)}`
}

/** "strong_seo" → "Strong SEO"; known reasons read as people say them. */
const REASON_LABEL: Record<string, string> = {
  strong_seo: "Strong SEO",
  too_early: "Raised too recently",
  late: "Raised too long ago",
  hardware: "Hardware",
  biotech: "Biotech",
  outside_us: "Outside the US",
  no_site: "No website",
  site_down: "Site down",
  excluded_industry: "Excluded industry",
  duplicate: "Already in the queue",
  too_big: "Too big",
  // The candidates job's own keys (outreach_candidates.py).
  outside_us_canada: "Outside the US and Canada",
  team_size_out_of_range: "Team size out of range",
  not_search_bought_industry: "Not bought through search",
  no_website: "No website",
  no_raise_found: "No raise found",
  raise_late: "Raised too long ago",
  raise_too_early: "Raised too recently",
  strong_seo_hiring_let_through: "Strong SEO (hiring let through)",
  excluded_keyword: "Excluded by keyword",
  // The source pulls (outreach_sources.py).
  not_tech_industry: "Not a tech filing",
  amount_out_of_range: "Amount outside $500K–$50M",
  fund_or_holding: "Fund or holding company",
  entity_type: "Not a corporation or LLC",
  amendment: "Amended filing",
  business_combination: "Acquisition, not a raise",
  name_not_software: "Name says not software",
  not_software: "Not a software company",
  domain_unresolved: "Website not found",
  domain_pending: "Website not checked yet",
  not_funding_news: "Not a funding story",
  not_us: "Outside the US",
  old_cohort: "Cohort too old",
  cohort_too_recent: "Cohort too recent",
}

export function reasonLabel(reason: string): string {
  if (REASON_LABEL[reason]) return REASON_LABEL[reason]
  const words = reason.replace(/[_-]+/g, " ").trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** The filter reasons, largest first, with the total. */
export function filteredBreakdown(stats: RunStats | undefined): { total: number; rows: { reason: string; label: string; count: number }[] } {
  const filtered = stats?.filtered ?? {}
  const rows = Object.entries(filtered)
    .map(([reason, count]) => ({ reason, label: reasonLabel(reason), count: Number(count) || 0 }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
  return { total: rows.reduce((n, r) => n + r.count, 0), rows }
}

/** A run's counters for the table: missing counters read as null, not 0. */
export function runCounts(run: Pick<OutreachRun, "stats">) {
  const n = (v: unknown) => (typeof v === "number" ? v : null)
  return {
    considered: n(run.stats?.considered),
    added: n(run.stats?.added),
    skipped_existing: n(run.stats?.skipped_existing),
    filtered: run.stats?.filtered ? filteredBreakdown(run.stats).total : null,
    researched: n(run.stats?.researched),
    drafted: n(run.stats?.drafted),
    added_to_backlog: n(run.stats?.added_to_backlog),
    queued_for_review: n(run.stats?.queued_for_review),
    refreshed: n(run.stats?.refreshed),
    skipped_from_backlog: n(run.stats?.skipped_from_backlog),
    backlog_size: n(run.stats?.backlog_size),
  }
}

/**
 * What a candidates run added, as one phrase: "312 to backlog · 20 to review"
 * for runs since the backlog, "20 added" for the ones before it; null when unknown.
 */
export function runAddedText(run: Pick<OutreachRun, "stats">): string | null {
  const c = runCounts(run)
  if (c.added_to_backlog !== null || c.queued_for_review !== null) {
    return `${(c.added_to_backlog ?? 0).toLocaleString("en-US")} to backlog · ${(c.queued_for_review ?? 0).toLocaleString("en-US")} to review`
  }
  return c.added !== null ? `${c.added} added` : null
}

/** A tool name as a short label: "dataforseo_labs" → "DataForSEO Labs". */
const TOOL_LABEL: Record<string, string> = {
  yc_api: "YC",
  yc: "YC",
  google_news: "Google News",
  dataforseo: "DataForSEO",
  dataforseo_labs: "DataForSEO Labs",
  dataforseo_serp: "DataForSEO SERP",
  fetch: "Fetch",
  sitemap: "Sitemap",
  claude: "Claude",
  perplexity: "Perplexity",
  chatgpt: "ChatGPT",
  hunter: "Hunter",
  linkedin: "LinkedIn",
  web: "Web",
  google: "Google",
  error: "Error",
  error_run_wide: "Error (whole pass)",
}

export function toolLabel(tool: string): string {
  return TOOL_LABEL[tool] ?? reasonLabel(tool)
}

/** A stable tone per tool so the same tool reads the same everywhere. */
export function toolTone(tool: string): StatusTone {
  if (tool.startsWith("error")) return "error"
  if (tool.startsWith("dataforseo")) return "info"
  if (tool.startsWith("yc")) return "warning"
  if (tool === "google_news") return "success"
  if (tool === "claude" || tool === "perplexity" || tool === "chatgpt") return "violet"
  return "neutral"
}

/** The tools in a list of queries, most used first: [{ tool, count, cost }]. */
export function toolCounts(queries: OutreachQuery[]) {
  const by = new Map<string, { tool: string; count: number; cost: number }>()
  for (const q of queries) {
    const row = by.get(q.tool) ?? { tool: q.tool, count: 0, cost: 0 }
    row.count += 1
    row.cost += q.cost_usd ?? 0
    by.set(q.tool, row)
  }
  return [...by.values()].sort((a, b) => b.count - a.count || a.tool.localeCompare(b.tool))
}

/** An error entry as one line of text. */
export function errorText(e: RunError): string {
  if (typeof e === "string") return e
  if (typeof e.message === "string") return e.message
  return JSON.stringify(e)
}

/**
 * The header's status line: "Last run today 7:02am, 2 added" and "Next Tue
 * 7:00am", or that the jobs are off.
 */
export function runStatusLine(summary: RunsSummary | undefined, now: Date = new Date()): { last: string | null; next: string | null; off: boolean } {
  if (!summary) return { last: null, next: null, off: false }
  const last = summary.last.candidates
  let lastText: string | null = null
  if (last?.started_at) {
    const bits = [`Last pull ${when(last.started_at, now)}`]
    if (last.status !== "ok") bits.push(RUN_STATUS[last.status].label.toLowerCase())
    const added = runAddedText(last)
    if (added) bits.push(added)
    lastText = bits.join(", ")
  }
  const next = summary.next.candidates
  return {
    last: lastText,
    next: summary.pipeline_enabled && next ? `Next ${upcoming(next, now)}` : null,
    off: !summary.pipeline_enabled,
  }
}

/** A future time as a schedule says it: "today 7:00pm", "tomorrow 7:00am", "Fri 7:00am", "Oct 20". */
export function upcoming(value: string, now: Date = new Date()) {
  const d = parse(value)
  const days = daysBetween(now, d)
  if (days <= 0) return `today ${clock(d)}`
  if (days === 1) return `tomorrow ${clock(d)}`
  if (days < 7) return `${WEEKDAYS[d.getDay()]} ${clock(d)}`
  return shortDate(value, now)
}

/* ------------------------------------------------------------------------ */
/* Settings                                                                  */
/* ------------------------------------------------------------------------ */

export const WEEKDAY_OPTIONS: { value: Weekday; label: string }[] = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
]

export const SOURCE_OPTIONS: { value: SourceKey; label: string; help: string }[] = [
  { value: "yc_new", label: "New YC batches", help: "Companies from every YC batch 45 days to the New YC window past Demo Day (the New YC list)." },
  { value: "yc_established", label: "Established YC companies", help: "Older YC companies, checked for a recent raise (the Just raised list)." },
  { value: "google_news", label: "Google News", help: "Raise announcements. Dates are wrong about 1 in 8, so they are marked verify." },
  { value: "form_d", label: "SEC Form D filings", help: "US tech companies that filed a new Form D ($500K to $50M sold), nightly. The raise is a government filing, so it is marked verified; only companies whose website was found and checked are kept." },
  { value: "news_feeds", label: "Funding news feeds", help: "TechCrunch, PR Newswire and Google News funding stories (and the July leads), read daily by Claude for US software raises." },
  { value: "accelerators", label: "Accelerator portfolios", help: "The newest cohorts of Techstars, a16z speedrun, Neo, South Park Commons and Antler, quarterly. Treated like new YC." },
]

/** The API's defaults for the backlog settings (OutreachSettings::DEFAULTS). */
export const NEW_YC_MAX_DAYS_DEFAULT = 365
export const NEWS_LOOKUPS_MAX_DEFAULT = 200

/** "Weekdays at 7:00am", "Mon, Wed, Fri at 7:00am". */
export function scheduleText(schedule: { days: Weekday[]; time: string }): string {
  const order = WEEKDAY_OPTIONS.map((o) => o.value)
  const days = [...schedule.days].sort((a, b) => order.indexOf(a) - order.indexOf(b))
  const [h, m] = schedule.time.split(":").map(Number)
  const time = `${h % 12 || 12}:${String(m).padStart(2, "0")}${h < 12 ? "am" : "pm"}`
  let label: string
  if (days.join() === "mon,tue,wed,thu,fri") label = "Weekdays"
  else if (days.length === 7) label = "Every day"
  else label = days.map((d) => WEEKDAY_OPTIONS.find((o) => o.value === d)?.label ?? d).join(", ")
  return `${label} at ${time}`
}

/** The values a form edits, taken from the GET response (read-only fields dropped). */
export function settingsValues(s: OutreachSettings): OutreachSettingsValues {
  return {
    pipeline_enabled: s.pipeline_enabled,
    daily_target: s.daily_target,
    split: { ...s.split },
    schedule: { days: [...s.schedule.days], time: s.schedule.time },
    research_interval_minutes: s.research_interval_minutes,
    raise_window_too_early_days: s.raise_window_too_early_days,
    raise_window_late_days: s.raise_window_late_days,
    strong_seo_keywords: s.strong_seo_keywords,
    sources: { ...s.sources },
    excluded_industries: [...s.excluded_industries],
    excluded_keywords: [...(s.excluded_keywords ?? [])],
    excluded_description_keywords: [...(s.excluded_description_keywords ?? [])],
    ankur_lines: [...s.ankur_lines],
    signature: s.signature,
    sender_email: s.sender_email,
    sender_name: s.sender_name,
    sending_paused: s.sending_paused,
    send_window: { ...s.send_window, days: [...s.send_window.days], hours: { ...(s.send_window.hours ?? {}) } },
    ramp_enabled: s.ramp_enabled ?? false,
    ramp: (s.ramp ?? []).map((r) => ({ ...r })),
    send_delay_minutes: s.send_delay_minutes,
    send_gap_minutes: s.send_gap_minutes,
    daily_send_cap: s.daily_send_cap,
    reply_delay_minutes: s.reply_delay_minutes,
    // An API from before the backlog does not send these yet: its pipeline's defaults.
    new_yc_max_days: s.new_yc_max_days ?? NEW_YC_MAX_DAYS_DEFAULT,
    news_lookups_max: s.news_lookups_max ?? NEWS_LOOKUPS_MAX_DEFAULT,
  }
}

/** Only the keys that changed, for a PUT. */
export function settingsChanges(before: OutreachSettingsValues, after: OutreachSettingsValues): Partial<OutreachSettingsValues> {
  const out: Partial<OutreachSettingsValues> = {}
  for (const key of Object.keys(after) as (keyof OutreachSettingsValues)[]) {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) (out as Record<string, unknown>)[key] = after[key]
  }
  return out
}

/** Checks the form can make before saving; the API checks the same. */
export function settingsProblems(v: OutreachSettingsValues): Partial<Record<keyof OutreachSettingsValues, string>> {
  const out: Partial<Record<keyof OutreachSettingsValues, string>> = {}
  if (v.split.just_raised + v.split.new_yc !== v.daily_target) out.split = `Just raised + New YC must add up to ${v.daily_target}.`
  if (v.raise_window_late_days <= v.raise_window_too_early_days) out.raise_window_late_days = "Late has to be more days than too early."
  if (!v.schedule.days.length) out.schedule = "Pick at least one day."
  if (!v.ankur_lines.filter((l) => l.trim()).length) out.ankur_lines = "Keep at least one line."
  if (!/^\S+@\S+\.\S+$/.test(v.sender_email)) out.sender_email = "Enter an email address."
  if (!v.sender_name.trim()) out.sender_name = "Enter the name founders see."
  if (!v.send_window.days.length) out.send_window = "Pick at least one sending day."
  else if (v.send_window.end <= v.send_window.start) out.send_window = "The window has to end after it starts."
  else if (Object.values(v.send_window.hours ?? {}).some((h) => h && h.end <= h.start)) out.send_window = "Each day's window has to end after it starts."
  const steps = v.ramp ?? []
  if (steps.some((r) => !/^\d{4}-\d{2}-\d{2}$/.test(r.from) || !(r.daily_target >= 1 && r.daily_target <= 500))) out.ramp = "Each step needs a date and 1 to 500 a day."
  else if (new Set(steps.map((r) => r.from)).size !== steps.length) out.ramp = "Two steps start on the same day."
  else if (v.ramp_enabled && !steps.length) out.ramp = "Add a step, or turn the ramp off."
  if (v.new_yc_max_days !== undefined && !(v.new_yc_max_days >= 60 && v.new_yc_max_days <= 1095)) out.new_yc_max_days = "Between 60 and 1,095 days."
  if (v.news_lookups_max !== undefined && !(v.news_lookups_max >= 0 && v.news_lookups_max <= 2000)) out.news_lookups_max = "Between 0 and 2,000 lookups."
  if (v.send_delay_minutes < 60) out.send_delay_minutes = "At least 60 minutes, so a wrong approval can be caught."
  return out
}

/* ------------------------------------------------------------------------ */
/* Sending                                                                   */
/* ------------------------------------------------------------------------ */

/** "Weekdays 8:00am–5:00pm, Los Angeles time". */
export function sendWindowText(w: SendWindow): string {
  const fmt = (t: string) => {
    const [h, m] = t.split(":").map(Number)
    return `${h % 12 || 12}:${String(m).padStart(2, "0")}${h < 12 ? "am" : "pm"}`
  }
  const zone = w.timezone.split("/").pop()?.replace(/_/g, " ") ?? w.timezone
  // Days that share hours read as one group: "Mon 12:00pm–5:00pm, Tue–Thu 8:00am–11:00am".
  const groups: { days: Weekday[]; hours: string }[] = []
  for (const d of WEEKDAY_OPTIONS.map((o) => o.value).filter((d) => w.days.includes(d))) {
    const h = dayHours(w, d)
    const hours = `${fmt(h.start)}–${fmt(h.end)}`
    const last = groups[groups.length - 1]
    if (last && last.hours === hours) last.days.push(d)
    else groups.push({ days: [d], hours })
  }
  return `${groups.map((g) => `${scheduleText({ days: g.days, time: "00:00" }).replace(/ at .*$/, "")} ${g.hours}`).join(", ")}, ${zone} time`
}

/** A day's hours: its own if the window gives them, else the window's. */
export function dayHours(w: SendWindow, day: Weekday): { start: string; end: string } {
  return w.hours?.[day] ?? { start: w.start, end: w.end }
}

const ZONE_SHORT: Record<string, string> = {
  "America/New_York": "ET", "America/Chicago": "CT", "America/Denver": "MT", "America/Los_Angeles": "PT", "Europe/London": "UK time",
}

/** "8–11am", "12–5pm", "9:30am–1pm". */
function shortHours(start: string, end: string): string {
  const part = (t: string) => {
    const [h, m] = t.split(":").map(Number)
    return { n: `${h % 12 || 12}${m ? `:${String(m).padStart(2, "0")}` : ""}`, ap: h < 12 ? "am" : "pm" }
  }
  const a = part(start)
  const b = part(end)
  return a.ap === b.ap ? `${a.n}–${b.n}${b.ap}` : `${a.n}${a.ap}–${b.n}${b.ap}`
}

/**
 * Where the send window stands now, for the Today screen's Ready to send group: today's hours,
 * whether it is open, or that today is not a send day. Cold emails only; LinkedIn and replies any day.
 */
export function sendWindowNow(w: SendWindow | undefined, now: Date = new Date()): { sendDay: boolean; open: boolean; text: string } | null {
  if (!w) return null
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: w.timezone, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ""
  const day = get("weekday").toLowerCase().slice(0, 3) as Weekday
  const time = `${get("hour")}:${get("minute")}`
  const zone = ZONE_SHORT[w.timezone] ?? w.timezone.split("/").pop()!.replace(/_/g, " ")
  if (!w.days.includes(day)) return { sendDay: false, open: false, text: "Not a send day, LinkedIn and replies only" }
  const h = dayHours(w, day)
  const open = time >= h.start && time < h.end
  const when = open ? "open now" : time < h.start ? "not open yet" : "closed for today"
  return { sendDay: true, open, text: `Send window: today ${shortHours(h.start, h.end)} ${zone} (${when})` }
}

/** "This week: 10/day · next: 15 from Oct 26"; null with the ramp off. */
export function rampText(status: RampStatus | undefined): string | null {
  if (!status?.enabled) return null
  const date = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
  const now = `This week: ${status.daily_target}/day`
  return status.next ? `${now} · next: ${status.next.daily_target} from ${date(status.next.from)}` : now
}

/** What a Gmail callback's ?gmail_error= means, said for the person who just tried. */
export function gmailErrorText(code: string, sender: string): string {
  switch (code) {
    case "wrong_account":
      return `That was a different Google account. Sign in as ${sender}.`
    case "missing_scopes":
      return "Google's screen had a box unticked. Connect again and leave both Gmail boxes ticked."
    case "access_denied":
      return "Canceled on Google's screen. Nothing was connected."
    case "org_internal":
      return `Google only lets accounts in Forge's own Workspace use this app, and ${sender} is outside it. The Google Cloud consent screen needs to be External.`
    case "expired_state":
    case "invalid_state":
      return "The connect link expired. Start again from this page."
    case "no_refresh_token":
      return "Google did not hand over offline access. Remove Forge under your Google account's third-party access, then connect again."
    default:
      return `Google said: ${code.replace(/_/g, " ")}.`
  }
}

/** Why Approve & send cannot run for this prospect, or null when it can. */
export function sendBlocker(
  p: Pick<OutreachProspect, "founder_email" | "email_subject" | "email_body">,
  gmail: Pick<GmailStatus, "connected" | "unavailable_reason"> | undefined,
): string | null {
  if (!p.founder_email) return "Add the founder's email address first."
  if (!p.email_subject || !p.email_body) return "The draft needs a subject and a body."
  if (!gmail) return "Checking Gmail…"
  if (!gmail.connected || gmail.unavailable_reason) return gmail.unavailable_reason ?? "Connect Gmail in Outreach settings first."
  return null
}

/** The thread as the panel shows it: oldest first, canceled ones dropped. */
export function threadMessages(messages: OutreachMessage[] | undefined): OutreachMessage[] {
  return [...(messages ?? [])].filter((m) => m.status !== "canceled").sort((a, b) => a.id - b.id)
}

/** The line over one message: who, and when it went, goes or came. */
export function messageMeta(m: OutreachMessage, now: Date = new Date()): string {
  const who =
    m.direction === "in" ? m.from_name || m.from_email || "Them" : m.source === "gmail" ? "Justin, from Gmail" : `Forge${m.created_by ? ` for ${m.created_by.name}` : ""}`
  switch (m.status) {
    case "scheduled":
      return `${who} · sends ${m.scheduled_for ? upcoming(m.scheduled_for, now) : "soon"}`
    case "sending":
      return `${who} · sending now`
    case "failed":
      return `${who} · not sent`
    default:
      return m.at ? `${who} · ${when(m.at, now)}` : who
  }
}

/* ------------------------------------------------------------------------ */
/* Fact sources                                                              */
/* ------------------------------------------------------------------------ */

const SOURCE_LABEL: Record<string, string> = {
  yc: "YC",
  yc_api: "YC",
  google_news: "Google News",
  techcrunch: "TechCrunch",
  dataforseo: "DataForSEO",
  dataforseo_labs: "DataForSEO",
  sitemap: "sitemap",
  crunchbase: "Crunchbase",
  linkedin: "LinkedIn",
  website: "their site",
  hunter: "Hunter",
}

export function sourceLabel(source: string): string {
  return SOURCE_LABEL[source] ?? source.replace(/_/g, " ")
}

/** The first source on record among these fields, for a facts-grid row. */
export function factSource(p: Pick<OutreachProspect, "fact_sources">, ...fields: string[]): FactSource | null {
  for (const f of fields) {
    const s = p.fact_sources?.[f]
    if (s?.source) return s
  }
  return null
}

/* ------------------------------------------------------------------------ */
/* Backlog                                                                   */
/* ------------------------------------------------------------------------ */

/** Backlog rows per page. */
export const BACKLOG_PAGE = 50

/** "12.4 days", "1 day", "under a day"; "–" without a target. */
export function daysOfSupply(days: number | null | undefined): string {
  if (days === null || days === undefined) return "–"
  if (days < 1) return days === 0 ? "0 days" : "under a day"
  const n = Number.isInteger(days) ? String(days) : days.toFixed(1)
  return `${n} ${n === "1" ? "day" : "days"}`
}

/** A batch chip's label: "X26" for "Spring 2026", "Not YC" for companies from the other sources. */
export function backlogBatchLabel(batch: string | null): string {
  return batch ? (batchShort(batch) ?? batch) : "Not YC"
}

/** Where a company was found, as people say it. */
export const PROSPECT_SOURCE: Record<string, string> = {
  yc: "YC",
  form_d: "SEC Form D",
  news: "Funding news",
  techstars: "Techstars",
  a16z_speedrun: "a16z speedrun",
  neo: "Neo",
  spc: "South Park Commons",
  antler: "Antler",
}

export function prospectSourceLabel(source: string): string {
  return PROSPECT_SOURCE[source] ?? source.replace(/_/g, " ")
}

/** The Backlog tab's source chips, biggest first (a company found in several sources counts under each). */
export function backlogSources(stats: Pick<BacklogStats, "by_source"> | undefined): { source: string; label: string; count: number }[] {
  return Object.entries(stats?.by_source ?? {})
    .map(([source, count]) => ({ source, label: prospectSourceLabel(source), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

/** A run's per-source rows for its table: found, filtered, merged, added and the Form D resolution rate. */
export function sourceRunRows(stats: Pick<RunStats, "sources"> | undefined): {
  source: string
  label: string
  found: number | null
  filtered: number
  reasons: { reason: string; label: string; count: number }[]
  duplicates: number | null
  added: number | null
  resolution: string | null
}[] {
  return Object.entries(stats?.sources ?? {}).map(([source, s]) => {
    const reasons = filteredBreakdown({ filtered: s.filtered ?? {} }).rows
    const r = s.resolution
    const rate = r && r.attempted ? `${r.resolved ?? 0} of ${r.attempted} (${Math.round(((r.resolved ?? 0) / r.attempted) * 100)}%)` : null
    return {
      source,
      label: prospectSourceLabel(source),
      found: typeof s.found === "number" ? s.found : null,
      filtered: reasons.reduce((n, x) => n + x.count, 0),
      reasons,
      duplicates: typeof s.duplicates === "number" ? s.duplicates : null,
      added: typeof s.added_to_backlog === "number" ? s.added_to_backlog : null,
      resolution: rate,
    }
  })
}

/**
 * The batch chips: one per batch, its lists' counts summed (a batch can hold
 * both lists), newest first as the API sends them.
 */
export function backlogBatches(stats: Pick<BacklogStats, "by_batch"> | undefined): { batch: string | null; label: string; count: number; lists: OutreachList[] }[] {
  const out: { batch: string | null; label: string; count: number; lists: OutreachList[] }[] = []
  for (const row of stats?.by_batch ?? []) {
    const hit = out.find((o) => o.batch === row.yc_batch)
    if (hit) {
      hit.count += row.count
      if (!hit.lists.includes(row.list_type)) hit.lists.push(row.list_type)
    } else {
      out.push({ batch: row.yc_batch, label: backlogBatchLabel(row.yc_batch), count: row.count, lists: [row.list_type] })
    }
  }
  return out
}

/** "51–100 of 1,240" for a page of the backlog. */
export function pageRange(page: number, perPage: number, total: number): string {
  if (!total) return "0 of 0"
  const from = (page - 1) * perPage + 1
  const to = Math.min(total, page * perPage)
  return `${from.toLocaleString("en-US")}–${to.toLocaleString("en-US")} of ${total.toLocaleString("en-US")}`
}
